/**
 * Utilidades para la imagen de cotización armada a mano: geometría de las
 * cotas (compartida entre el editor SVG y la exportación en canvas, para que
 * lo que se ve al editar sea lo mismo que sale en la imagen) y composición de
 * la imagen final (tabla de medidas a la izquierda, fotos a la derecha).
 */

export const COLOR_COTA = '#d42a20'
export const MAX_FOTOS_COTIZACION = 4

// Tamaño de la imagen final. Proporción similar a la de la referencia.
export const SALIDA_ANCHO = 2400
export const SALIDA_ALTO = 1600
const SALIDA_PADDING = 64
const SALIDA_GAP = 40
const LEYENDA_FRACCION = 0.27

// Ángulo (grados) dentro del cual un trazo se endereza a horizontal/vertical.
const SNAP_GRADOS = 7

/** Unidad base de dibujo según el tamaño de la foto: así la cota se ve igual en fotos grandes y chicas. */
export const unidadCota = (ancho, alto) => Math.max(ancho, alto) / 1000

/** Endereza el punto final a horizontal o vertical si el trazo está casi alineado. */
export const ajustarAEje = (x1, y1, x2, y2) => {
  const dx = x2 - x1
  const dy = y2 - y1
  const tolerancia = Math.tan((SNAP_GRADOS * Math.PI) / 180)
  if (Math.abs(dy) <= Math.abs(dx) * tolerancia) return { x: x2, y: y1 }
  if (Math.abs(dx) <= Math.abs(dy) * tolerancia) return { x: x1, y: y2 }
  return { x: x2, y: y2 }
}

// Estilo de una cota cuando no se ha cambiado: roja, grosor normal, flecha en
// ambas puntas y sin líneas guía. `puntaA` es la punta izquierda (arriba si la
// cota es vertical) y `puntaB` la derecha (abajo). `guiasLado` no va aquí: sin
// elegir, las guías salen al lado contrario de la letra.
export const ESTILO_COTA_DEFECTO = {
  color: COLOR_COTA,
  grosor: 1,
  puntaA: 'flecha',
  puntaB: 'flecha',
  guias: 0,
  guiasEstilo: 'discontinua',
  letraTamano: 1,
  // 'medida' = línea con la letra al lado; 'globo' = letra dentro de un
  // círculo con una línea que apunta a la pieza (x1,y1 = punto en la pieza,
  // x2,y2 = centro del globo); 'circunferencia' = círculo (x1,y1 = centro,
  // x2,y2 = borde) con la letra fuera.
  tipo: 'medida',
  lineaGlobo: 'codo',
  puntaGlobo: 'flecha',
  // Trazo del círculo cuando tipo = 'circunferencia'.
  trazoCircunferencia: 'continua',
}

/** true si la cota se lee de arriba a abajo (más alta que ancha). */
export const cotaEsVertical = ({ x1, y1, x2, y2 }) => Math.abs(y2 - y1) > Math.abs(x2 - x1)
// Largo inicial (en unidades de cota) al activar las líneas guía.
export const GUIAS_LARGO_DEFECTO = 120

// Colores claros llevan halo oscuro para que se lean sobre fondo blanco.
const esColorClaro = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '')
  if (!m) return false
  const n = parseInt(m[1], 16)
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255
  return lum > 0.75
}

/**
 * Geometría de un globo: círculo con la letra en (x2,y2) y línea desde su
 * borde hasta el punto de la pieza (x1,y1), recta o en codo (primero
 * horizontal, luego vertical). La punta va en la pieza.
 */
const geometriaGlobo = ({ x1, y1, x2, y2 }, estilo, s) => {
  const factorPunta = 0.5 + 0.5 * estilo.grosor
  const grosor = 3.4 * s * estilo.grosor
  const r = 30 * s * estilo.letraTamano
  const claro = esColorClaro(estilo.color)
  const dx = x1 - x2
  const dy = y1 - y2

  let recorrido
  if (estilo.lineaGlobo === 'codo' && Math.abs(dx) > r && Math.abs(dy) > r) {
    recorrido = [[x2 + Math.sign(dx) * r, y2], [x1, y2], [x1, y1]]
  } else {
    const d = Math.hypot(dx, dy) || 1
    recorrido = [[x2 + (dx / d) * r, y2 + (dy / d) * r], [x1, y1]]
  }
  const lineas = recorrido.slice(1).map((p, i) => [...recorrido[i], ...p])

  // Dirección del último tramo, para orientar la punta hacia la pieza.
  const [ax, ay] = recorrido[recorrido.length - 2]
  const largo = Math.hypot(x1 - ax, y1 - ay) || 1
  const ux = (x1 - ax) / largo
  const uy = (y1 - ay) / largo
  const fl = Math.min(18 * s * factorPunta, largo / 2)
  const fa = 7 * s * factorPunta

  return {
    linea: lineas[lineas.length - 1],
    lineas,
    flechas:
      estilo.puntaGlobo === 'flecha'
        ? [[[x1, y1], [x1 - ux * fl - uy * fa, y1 - uy * fl + ux * fa], [x1 - ux * fl + uy * fa, y1 - uy * fl - ux * fa]]]
        : [],
    puntos: estilo.puntaGlobo === 'redonda' ? [[x1, y1]] : [],
    radioPunto: 6 * s * factorPunta,
    topes: [],
    guias: [],
    guiaGrosor: grosor * 0.6,
    guiaGuiones: null,
    etiqueta: { x: x2, y: y2, fuente: 34 * s * estilo.letraTamano },
    globo: { x: x2, y: y2, r, relleno: claro ? '#1f2328' : '#ffffff' },
    circunferencia: null,
    color: estilo.color,
    halo: 7 * s,
    colorHalo: claro ? '#1f2328' : '#ffffff',
    colorHaloLinea: claro ? 'rgba(31,35,40,0.6)' : 'rgba(255,255,255,0.75)',
    grosor,
  }
}

/**
 * Geometría de una circunferencia: círculo con centro en (x1,y1) y borde en
 * (x2,y2). La letra queda fuera según `lado` (1 = arriba, -1 = abajo).
 */
const geometriaCircunferencia = ({ x1, y1, x2, y2, lado = 1 }, estilo, s) => {
  const r = Math.hypot(x2 - x1, y2 - y1) || 1
  const fuente = 46 * s * estilo.letraTamano
  const separacion = 34 * s * estilo.letraTamano
  const grosor = 3.4 * s * estilo.grosor
  const claro = esColorClaro(estilo.color)
  // Letra arriba (lado 1) o abajo (lado -1), centrada horizontalmente.
  const ny = lado > 0 ? -1 : 1
  return {
    linea: [x1, y1, x2, y2],
    lineas: [],
    flechas: [],
    puntos: [],
    radioPunto: 6 * s,
    topes: [],
    guias: [],
    guiaGrosor: grosor * 0.6,
    guiaGuiones: null,
    etiqueta: {
      x: x1,
      y: y1 + ny * (r + separacion),
      fuente,
    },
    globo: null,
    circunferencia: { x: x1, y: y1, r },
    circunferenciaGuiones: estilo.trazoCircunferencia === 'discontinua' ? [12 * s, 9 * s] : null,
    color: estilo.color,
    halo: 7 * s,
    colorHalo: claro ? '#1f2328' : '#ffffff',
    colorHaloLinea: claro ? 'rgba(31,35,40,0.6)' : 'rgba(255,255,255,0.75)',
    grosor,
  }
}

/**
 * Geometría de una cota: línea, puntas (flecha, redonda o ninguna), topes
 * perpendiculares, líneas guía opcionales y posición de la letra. `lado`
 * (1 | -1) indica a qué lado de la línea va la letra; las guías van al lado
 * contrario (hacia la pieza) y sobresalen un poco del lado de la letra.
 */
export const geometriaCota = (cota, s) => {
  const { x1, y1, x2, y2, lado = 1 } = cota
  const estilo = { ...ESTILO_COTA_DEFECTO, ...cota }
  if (estilo.tipo === 'globo') return geometriaGlobo(cota, estilo, s)
  if (estilo.tipo === 'circunferencia') return geometriaCircunferencia(cota, estilo, s)
  const dx = x2 - x1
  const dy = y2 - y1
  const largo = Math.hypot(dx, dy) || 1
  const ux = dx / largo
  const uy = dy / largo
  // Normal que apunta "hacia arriba" (o a la izquierda si la línea es vertical).
  let nx = -uy
  let ny = ux
  if (ny > 0 || (ny === 0 && nx > 0)) {
    nx = -nx
    ny = -ny
  }

  const factorPunta = 0.5 + 0.5 * estilo.grosor
  const flechaLargo = Math.min(18 * s * factorPunta, largo / 3)
  const flechaAncho = 7 * s * factorPunta
  const flecha = (px, py, dirX, dirY) => [
    [px, py],
    [px - dirX * flechaLargo + nx * flechaAncho, py - dirY * flechaLargo + ny * flechaAncho],
    [px - dirX * flechaLargo - nx * flechaAncho, py - dirY * flechaLargo - ny * flechaAncho],
  ]

  const tope = 14 * s
  const fuente = 46 * s * estilo.letraTamano
  const separacion = 34 * s * estilo.letraTamano
  const grosor = 3.4 * s * estilo.grosor

  // Guías: perpendiculares en cada extremo. `guiasLado` 1 = hacia la normal
  // (arriba/izquierda), -1 = al contrario, 0 = ambos lados. Sin elegir van al
  // lado contrario de la letra (hacia la pieza) y sobresalen un poco del otro.
  const guiaLargo = (estilo.guias || 0) * s
  const guiaSobra = 12 * s
  const guiasLado = estilo.guiasLado ?? -lado
  const guia = (px, py) => {
    if (guiasLado === 0) {
      return [px - nx * guiaLargo, py - ny * guiaLargo, px + nx * guiaLargo, py + ny * guiaLargo]
    }
    const gx = nx * guiasLado
    const gy = ny * guiasLado
    return [px - gx * guiaSobra, py - gy * guiaSobra, px + gx * guiaLargo, py + gy * guiaLargo]
  }
  const claro = esColorClaro(estilo.color)

  // Punta de cada extremo según su lado visual (izquierda/derecha o arriba/abajo),
  // no según el orden en que se trazó.
  const p1EsA = cotaEsVertical(cota) ? y1 <= y2 : x1 <= x2
  const extremos = [
    { x: x1, y: y1, dirX: -ux, dirY: -uy, punta: p1EsA ? estilo.puntaA : estilo.puntaB },
    { x: x2, y: y2, dirX: ux, dirY: uy, punta: p1EsA ? estilo.puntaB : estilo.puntaA },
  ]

  return {
    linea: [x1, y1, x2, y2],
    lineas: [[x1, y1, x2, y2]],
    globo: null,
    circunferencia: null,
    flechas: extremos.filter((e) => e.punta === 'flecha').map((e) => flecha(e.x, e.y, e.dirX, e.dirY)),
    puntos: extremos.filter((e) => e.punta === 'redonda').map((e) => [e.x, e.y]),
    radioPunto: 6 * s * factorPunta,
    // Con guías los topes sobran: la guía ya marca el extremo.
    topes: guiaLargo
      ? []
      : extremos
          .filter((e) => e.punta === 'flecha')
          .map((e) => [e.x + nx * tope, e.y + ny * tope, e.x - nx * tope, e.y - ny * tope]),
    guias: guiaLargo ? [guia(x1, y1), guia(x2, y2)] : [],
    guiaGrosor: grosor * 0.6,
    guiaGuiones: estilo.guiasEstilo === 'continua' ? null : [10 * s, 7 * s],
    etiqueta: {
      x: (x1 + x2) / 2 + nx * separacion * lado,
      y: (y1 + y2) / 2 + ny * separacion * lado,
      fuente,
    },
    color: estilo.color,
    halo: 7 * s,
    colorHalo: claro ? '#1f2328' : '#ffffff',
    colorHaloLinea: claro ? 'rgba(31,35,40,0.6)' : 'rgba(255,255,255,0.75)',
    grosor,
  }
}

/** Dibuja una cota en un contexto 2D, en coordenadas de la foto. */
export const dibujarCota = (ctx, cota, letra, s) => {
  const g = geometriaCota(cota, s)
  ctx.save()
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  const segmentos = (lista) => {
    ctx.beginPath()
    lista.forEach(([ax, ay, bx, by]) => {
      ctx.moveTo(ax, ay)
      ctx.lineTo(bx, by)
    })
    ctx.stroke()
  }
  // Halo para que la cota se lea sobre cualquier fondo.
  const trazar = (extra, estilo) => {
    ctx.strokeStyle = estilo
    if (g.guias.length) {
      ctx.lineWidth = g.guiaGrosor + extra
      ctx.setLineDash(g.guiaGuiones || [])
      segmentos(g.guias)
      ctx.setLineDash([])
    }
    ctx.lineWidth = g.grosor + extra
    segmentos([...g.lineas, ...g.topes])
    if (g.globo) {
      ctx.beginPath()
      ctx.arc(g.globo.x, g.globo.y, g.globo.r, 0, Math.PI * 2)
      ctx.stroke()
    }
    if (g.circunferencia) {
      ctx.setLineDash(g.circunferenciaGuiones || [])
      ctx.beginPath()
      ctx.arc(g.circunferencia.x, g.circunferencia.y, g.circunferencia.r, 0, Math.PI * 2)
      ctx.stroke()
      ctx.setLineDash([])
    }
  }
  trazar(g.halo * 0.6, g.colorHaloLinea)
  if (g.globo) {
    ctx.fillStyle = g.globo.relleno
    ctx.beginPath()
    ctx.arc(g.globo.x, g.globo.y, g.globo.r, 0, Math.PI * 2)
    ctx.fill()
  }
  trazar(0, g.color)

  ctx.fillStyle = g.color
  g.flechas.forEach((puntos) => {
    ctx.beginPath()
    ctx.moveTo(puntos[0][0], puntos[0][1])
    ctx.lineTo(puntos[1][0], puntos[1][1])
    ctx.lineTo(puntos[2][0], puntos[2][1])
    ctx.closePath()
    ctx.fill()
  })
  g.puntos.forEach(([px, py]) => {
    ctx.beginPath()
    ctx.arc(px, py, g.radioPunto, 0, Math.PI * 2)
    ctx.fill()
  })

  ctx.font = `800 ${g.etiqueta.fuente}px "Helvetica Neue", Arial, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  // Dentro del globo la letra ya tiene fondo: no lleva halo.
  if (!g.globo) {
    ctx.lineWidth = g.halo
    ctx.strokeStyle = g.colorHalo
    ctx.strokeText(letra, g.etiqueta.x, g.etiqueta.y)
  }
  ctx.fillText(letra, g.etiqueta.x, g.etiqueta.y)
  ctx.restore()
}

const decodificarImagen = (src, crossOrigin) =>
  new Promise((resolve, reject) => {
    const image = new window.Image()
    if (crossOrigin) image.crossOrigin = crossOrigin
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('No se pudo cargar la imagen'))
    image.src = src
  })

/**
 * Carga una foto de forma que se pueda pintar en un canvas y exportar sin que
 * quede "tainted". Lanza error si el servidor no entrega la foto con CORS.
 * `revocar` indica si `objectUrl` es un blob local que hay que liberar.
 */
export const cargarImagenExportable = async (url) => {
  // 1) Descarga sin caché: si un <img> normal ya cargó la foto, la caché puede
  //    tener la respuesta sin cabeceras CORS y el canvas quedaría bloqueado.
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store' })
    if (response.ok) {
      const objectUrl = URL.createObjectURL(await response.blob())
      try {
        const img = await decodificarImagen(objectUrl)
        return { objectUrl, ancho: img.naturalWidth, alto: img.naturalHeight, img, revocar: true }
      } catch (error) {
        URL.revokeObjectURL(objectUrl)
        throw error
      }
    }
  } catch (error) {
    console.warn('Descarga CORS de la foto falló, se intenta con <img crossOrigin>:', error)
  }
  // 2) <img crossOrigin="anonymous">: también deja el canvas exportable
  const img = await decodificarImagen(url, 'anonymous')
  return { objectUrl: url, ancho: img.naturalWidth, alto: img.naturalHeight, img, revocar: false }
}

/**
 * Saca el "gs://bucket/objeto" de una URL firmada de Cloud Storage, para poder
 * volver a firmarla con /v1/revisiones/firmar (esas URLs sí traen CORS).
 */
export const gsUrlDesdeUrlFirmada = (url) => {
  try {
    const { hostname, pathname } = new URL(url)
    const ruta = pathname.replace(/^\/+/, '')
    if (hostname === 'storage.googleapis.com' || hostname === 'storage.cloud.google.com') {
      const [bucket, ...objeto] = ruta.split('/')
      return bucket && objeto.length ? `gs://${bucket}/${decodeURIComponent(objeto.join('/'))}` : null
    }
    const sufijo = '.storage.googleapis.com'
    if (hostname.endsWith(sufijo) && ruta) {
      return `gs://${hostname.slice(0, -sufijo.length)}/${decodeURIComponent(ruta)}`
    }
  } catch {
    // URL inválida
  }
  return null
}

/**
 * Cuadrícula de celdas iguales: 1 foto ocupa todo, 2 van mitad y mitad, 3 y 4
 * van en 2×2 (con 3 la última celda queda vacía).
 */
const distribuirCeldas = (n, area) => {
  const g = SALIDA_GAP
  const columnas = n === 1 ? 1 : 2
  const filas = n <= 2 ? 1 : 2
  const w = (area.w - g * (columnas - 1)) / columnas
  const h = (area.h - g * (filas - 1)) / filas
  return Array.from({ length: n }, (_, i) => ({
    x: area.x + (i % columnas) * (w + g),
    y: area.y + Math.floor(i / columnas) * (h + g),
    w,
    h,
  }))
}

/** Parte un texto en líneas que quepan en `anchoMax` con la fuente actual del contexto. */
const partirTexto = (ctx, texto, anchoMax) => {
  const palabras = String(texto).split(/\s+/).filter(Boolean)
  const lineas = []
  let actual = ''
  palabras.forEach((palabra) => {
    const prueba = actual ? `${actual} ${palabra}` : palabra
    if (ctx.measureText(prueba).width <= anchoMax || !actual) {
      actual = prueba
    } else {
      lineas.push(actual)
      actual = palabra
    }
  })
  if (actual) lineas.push(actual)
  return lineas.length ? lineas : ['']
}

const FUENTE_TABLA = '"Helvetica Neue", Arial, sans-serif'
const TABLA_BORDE = '#d5d9e0'
const TABLA_ENCABEZADO = '#1f2328'
const TABLA_CEBRA = '#f5f6f8'
const TABLA_LETRA_INFORMATIVA = '#9ca3af'

/** Calcula columnas y filas de la tabla de medidas con un tamaño de fuente dado. */
const maquetarTabla = (ctx, filas, fuente, anchoTotal) => {
  const pad = fuente * 0.6
  const interlineado = fuente * 1.25
  ctx.font = `700 ${fuente}px ${FUENTE_TABLA}`
  const encabezados = ['MEDIDA', 'DESCRIPCIÓN', 'VALOR (mm)']
  const anchoLetra = Math.max(ctx.measureText(encabezados[0]).width, fuente * 1.6) + pad * 2
  const anchoEncabezadoValor = ctx.measureText(encabezados[2]).width
  ctx.font = `600 ${fuente}px ${FUENTE_TABLA}`
  const anchoValor = Math.min(
    anchoTotal * 0.32,
    Math.max(fuente * 3.2, anchoEncabezadoValor, ...filas.map((f) => ctx.measureText(f.valor).width)) + pad * 2,
  )
  const anchoDesc = anchoTotal - anchoLetra - anchoValor
  ctx.font = `500 ${fuente}px ${FUENTE_TABLA}`
  const altoEncabezado = fuente * 1.25 + pad * 2
  let alto = altoEncabezado
  const filasMaquetadas = filas.map((f) => {
    const lineas = partirTexto(ctx, f.texto, anchoDesc - pad * 2)
    ctx.font = `600 ${fuente}px ${FUENTE_TABLA}`
    const lineasValor = partirTexto(ctx, f.valor, anchoValor - pad * 2)
    ctx.font = `500 ${fuente}px ${FUENTE_TABLA}`
    const altoFila = Math.max(lineas.length, lineasValor.length, 1) * interlineado + pad * 2
    alto += altoFila
    return { ...f, lineas, lineasValor, alto: altoFila }
  })
  return {
    fuente, pad, interlineado, encabezados, anchoLetra, anchoValor, anchoDesc, altoEncabezado, alto,
    filas: filasMaquetadas,
  }
}

/** Dibuja la tabla de medidas (letra | descripción | valor opcional). */
const dibujarTabla = (ctx, filas, x, yArea, anchoTotal, altoArea) => {
  let t = maquetarTabla(ctx, filas, 20, anchoTotal)
  while (t.alto > altoArea && t.fuente > 14) t = maquetarTabla(ctx, filas, t.fuente - 2, anchoTotal)
  const { fuente, pad, interlineado, anchoLetra, anchoValor, anchoDesc } = t
  let y = yArea + Math.max(0, (altoArea - t.alto) / 2)
  const yInicio = y
  const columnas = [
    { x, w: anchoLetra, centrado: true },
    { x: x + anchoLetra, w: anchoDesc, centrado: false },
    { x: x + anchoLetra + anchoDesc, w: anchoValor, centrado: true },
  ]

  ctx.textBaseline = 'top'
  // Encabezado
  ctx.fillStyle = TABLA_ENCABEZADO
  ctx.fillRect(x, y, anchoTotal, t.altoEncabezado)
  ctx.fillStyle = '#ffffff'
  ctx.font = `700 ${fuente}px ${FUENTE_TABLA}`
  t.encabezados.forEach((titulo, i) => {
    const c = columnas[i]
    ctx.textAlign = c.centrado ? 'center' : 'left'
    ctx.fillText(titulo, c.centrado ? c.x + c.w / 2 : c.x + pad, y + pad)
  })
  y += t.altoEncabezado

  // Filas
  t.filas.forEach((f, idx) => {
    if (idx % 2 === 1) {
      ctx.fillStyle = TABLA_CEBRA
      ctx.fillRect(x, y, anchoTotal, f.alto)
    }
    const yTexto = (lineas) => y + (f.alto - lineas * interlineado) / 2 + (interlineado - fuente) / 2
    ctx.font = `800 ${fuente * 1.05}px ${FUENTE_TABLA}`
    // Letra roja si la medida está trazada en alguna foto; gris si solo es informativa.
    ctx.fillStyle = f.trazada === false ? TABLA_LETRA_INFORMATIVA : COLOR_COTA
    ctx.textAlign = 'center'
    ctx.fillText(f.letra, columnas[0].x + columnas[0].w / 2, yTexto(1))

    ctx.font = `500 ${fuente}px ${FUENTE_TABLA}`
    ctx.fillStyle = '#1f2328'
    ctx.textAlign = 'left'
    const y0 = yTexto(f.lineas.length)
    f.lineas.forEach((linea, i) => ctx.fillText(linea, columnas[1].x + pad, y0 + i * interlineado))

    ctx.font = `600 ${fuente}px ${FUENTE_TABLA}`
    ctx.textAlign = 'center'
    const yv = yTexto(f.lineasValor.length)
    f.lineasValor.forEach((linea, i) => ctx.fillText(linea, columnas[2].x + columnas[2].w / 2, yv + i * interlineado))
    y += f.alto
    ctx.strokeStyle = TABLA_BORDE
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + anchoTotal, y)
    ctx.stroke()
  })

  // Marco y divisiones verticales
  ctx.strokeStyle = TABLA_BORDE
  ctx.lineWidth = 2
  ctx.strokeRect(x, yInicio, anchoTotal, y - yInicio)
  ctx.beginPath()
  columnas.slice(1).forEach((c) => {
    ctx.moveTo(c.x, yInicio + t.altoEncabezado)
    ctx.lineTo(c.x, y)
  })
  ctx.stroke()
}

// ==================== Recorte automático al contenido ====================
// Lado de la muestra reducida con la que se detecta el contenido.
const MUESTRA_RECORTE = 400
// Diferencia máxima (0-255 por canal) con el color del fondo para seguir
// considerando un píxel como fondo.
const TOLERANCIA_FONDO = 28
// Margen alrededor del contenido, como fracción de su lado mayor.
const MARGEN_RECORTE = 0.03

/** Caja (en coordenadas de la foto) que ocupa una cota con letra, puntas y guías. */
export const cajaCota = (cota, s, letra = '') => {
  const g = geometriaCota(cota, s)
  const puntos = [
    ...g.flechas.flat(),
    ...[...g.lineas, ...g.topes, ...g.guias].flatMap(([ax, ay, bx, by]) => [[ax, ay], [bx, by]]),
  ]
  if (g.globo) {
    const { x: gx, y: gy, r: gr } = g.globo
    puntos.push([gx - gr, gy - gr], [gx + gr, gy + gr])
  }
  if (g.circunferencia) {
    const { x: cx, y: cy, r: cr } = g.circunferencia
    puntos.push([cx - cr, cy - cr], [cx + cr, cy + cr])
  }
  const r = g.radioPunto
  g.puntos.forEach(([px, py]) => puntos.push([px - r, py - r], [px + r, py + r]))
  const { x, y, fuente } = g.etiqueta
  // Las letras numeradas (A1, A2...) ocupan más ancho que una sola letra.
  const medioAncho = fuente * (0.1 + 0.35 * Math.max(1, letra.length))
  puntos.push([x - medioAncho, y - fuente * 0.6], [x + medioAncho, y + fuente * 0.6])
  const pad = g.halo + g.grosor
  const xs = puntos.map((p) => p[0])
  const ys = puntos.map((p) => p[1])
  return { x0: Math.min(...xs) - pad, y0: Math.min(...ys) - pad, x1: Math.max(...xs) + pad, y1: Math.max(...ys) + pad }
}

/**
 * Recorte que deja solo la pieza y sus cotas, sin el margen de fondo de la
 * foto. El fondo se toma del color de las esquinas (o de la transparencia si
 * se le quitó el fondo). Devuelve `{ x, y, w, h }` en coordenadas de la foto,
 * o null si no se pudo leer la imagen.
 */
export const calcularRecorte = (foto) => {
  const { img, ancho: W, alto: H } = foto
  const k = Math.min(1, MUESTRA_RECORTE / Math.max(W, H))
  const w = Math.max(1, Math.round(W * k))
  const h = Math.max(1, Math.round(H * k))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  ctx.drawImage(img, 0, 0, w, h)
  let datos
  try {
    datos = ctx.getImageData(0, 0, w, h).data
  } catch {
    return null
  }

  const pixel = (x, y) => {
    const i = (y * w + x) * 4
    return [datos[i], datos[i + 1], datos[i + 2], datos[i + 3]]
  }
  const esquinas = [pixel(0, 0), pixel(w - 1, 0), pixel(0, h - 1), pixel(w - 1, h - 1)]
  const fondo = [0, 1, 2, 3].map((c) => esquinas.reduce((acc, p) => acc + p[c], 0) / 4)
  const fondoTransparente = fondo[3] < 20

  let minX = w
  let minY = h
  let maxX = -1
  let maxY = -1
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = (y * w + x) * 4
      if (datos[i + 3] < 20) continue
      const contenido =
        fondoTransparente ||
        Math.max(
          Math.abs(datos[i] - fondo[0]),
          Math.abs(datos[i + 1] - fondo[1]),
          Math.abs(datos[i + 2] - fondo[2]),
        ) > TOLERANCIA_FONDO
      if (contenido) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }

  // Pieza + margen, sin salirse de la foto.
  let caja = { x0: 0, y0: 0, x1: W, y1: H }
  if (maxX >= 0) {
    const x0 = minX / k
    const y0 = minY / k
    const x1 = (maxX + 1) / k
    const y1 = (maxY + 1) / k
    const m = MARGEN_RECORTE * Math.max(x1 - x0, y1 - y0)
    caja = { x0: Math.max(0, x0 - m), y0: Math.max(0, y0 - m), x1: Math.min(W, x1 + m), y1: Math.min(H, y1 + m) }
  }

  // Las cotas siempre caben, aunque queden fuera de la pieza o de la foto.
  const s = unidadCota(W, H)
  Object.entries(foto.cotas || {}).forEach(([letra, cota]) => {
    const c = cajaCota(cota, s, letra)
    caja = {
      x0: Math.min(caja.x0, c.x0),
      y0: Math.min(caja.y0, c.y0),
      x1: Math.max(caja.x1, c.x1),
      y1: Math.max(caja.y1, c.y1),
    }
  })
  return { x: caja.x0, y: caja.y0, w: caja.x1 - caja.x0, h: caja.y1 - caja.y0 }
}

// ==================== Textos libres sobre la imagen final ====================
export const FUENTE_TEXTO = FUENTE_TABLA
export const INTERLINEA_TEXTO = 1.2
export const TEXTO_DEFECTO = { texto: 'Texto', tamano: 48, color: '#1f2328', negrita: true, fondo: false }

/** Relleno de la caja del texto cuando lleva fondo (en px de la imagen final). */
export const paddingTexto = (t) => (t.fondo ? t.tamano * 0.25 : 0)

/**
 * Dibuja un texto (posiblemente de varias líneas) con su esquina superior
 * izquierda en (x, y). Mismas métricas que la vista previa HTML: interlineado
 * INTERLINEA_TEXTO y, con fondo, una caja blanca que sobresale `paddingTexto`.
 */
export const dibujarTexto = (ctx, t) => {
  const lineas = String(t.texto ?? '').split('\n')
  const alto = t.tamano * INTERLINEA_TEXTO
  ctx.save()
  ctx.font = `${t.negrita ? 700 : 500} ${t.tamano}px ${FUENTE_TEXTO}`
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  if (t.fondo) {
    const pad = paddingTexto(t)
    const ancho = Math.max(0, ...lineas.map((l) => ctx.measureText(l).width))
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
    ctx.fillRect(t.x - pad, t.y - pad, ancho + pad * 2, alto * lineas.length + pad * 2)
  }
  ctx.fillStyle = t.color
  lineas.forEach((linea, i) => ctx.fillText(linea, t.x, t.y + i * alto + (alto - t.tamano) / 2))
  ctx.restore()
}

/** Tamaño visible de la foto: el del recorte si tiene, o el de la foto completa. */
export const tamanoVisible = (foto) =>
  foto.recorte ? { w: foto.recorte.w, h: foto.recorte.h } : { w: foto.ancho, h: foto.alto }

/**
 * Dibuja la foto con sus cotas en coordenadas visibles (0..w, 0..h de
 * `tamanoVisible`): si la foto tiene `recorte`, solo se ve esa zona.
 * El llamador escala el contexto al tamaño en que la quiere.
 */
export const dibujarFotoConCotas = (ctx, foto) => {
  const { w, h } = tamanoVisible(foto)
  const r = foto.recorte || { x: 0, y: 0 }
  ctx.save()
  ctx.beginPath()
  ctx.rect(0, 0, w, h)
  ctx.clip()
  ctx.translate(-r.x, -r.y)
  ctx.drawImage(foto.img, 0, 0, foto.ancho, foto.alto)
  // La unidad de cota sale de la foto completa: recortar no cambia su tamaño.
  const s = unidadCota(foto.ancho, foto.alto)
  Object.entries(foto.cotas || {}).forEach(([letra, cota]) => dibujarCota(ctx, cota, letra, s))
  ctx.restore()
}

/** Fondo blanco y tabla de medidas a la izquierda. */
export const dibujarFondoCotizacion = (ctx, leyenda) => {
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, SALIDA_ANCHO, SALIDA_ALTO)
  const anchoTabla = SALIDA_ANCHO * LEYENDA_FRACCION - SALIDA_PADDING * 1.5
  dibujarTabla(ctx, leyenda, SALIDA_PADDING, SALIDA_PADDING, anchoTabla, SALIDA_ALTO - SALIDA_PADDING * 2)
}

/**
 * Posición por defecto de cada foto (en px de la imagen final): a la derecha
 * de la tabla, en cuadrícula de celdas iguales, centrada y sin deformar.
 */
export const posicionesIniciales = (fotos) => {
  const xFotos = SALIDA_ANCHO * LEYENDA_FRACCION
  const area = {
    x: xFotos,
    y: SALIDA_PADDING,
    w: SALIDA_ANCHO - xFotos - SALIDA_PADDING,
    h: SALIDA_ALTO - SALIDA_PADDING * 2,
  }
  const celdas = distribuirCeldas(fotos.length, area)
  return fotos.map((foto, i) => {
    const celda = celdas[i]
    const visible = tamanoVisible(foto)
    const escala = Math.min(celda.w / visible.w, celda.h / visible.h)
    const w = visible.w * escala
    const h = visible.h * escala
    return { x: celda.x + (celda.w - w) / 2, y: celda.y + (celda.h - h) / 2, w, h }
  })
}

/**
 * Compone la imagen final: tabla de medidas (con valores) a la izquierda y las fotos con
 * sus cotas en `posiciones` (por defecto, la cuadrícula de `posicionesIniciales`).
 * Las fotos se dibujan en el orden del arreglo: la última queda encima. Devuelve un Blob JPEG.
 *
 * @param {{ img: HTMLImageElement, ancho: number, alto: number, cotas: Object<string, object> }[]} fotos
 * @param {{ letra: string, texto: string, valor?: string }[]} leyenda
 * @param {{ x: number, y: number, w: number, h: number }[]} [posiciones] una por foto, mismo orden
 * @param {{ texto: string, x: number, y: number, tamano: number, color: string, negrita: boolean, fondo: boolean }[]} [textos]
 *   textos libres, en px de la imagen final
 */
export const generarImagenCotizacion = async (
  fotos,
  leyenda,
  posiciones = posicionesIniciales(fotos),
  textos = [],
) => {
  const canvas = document.createElement('canvas')
  canvas.width = SALIDA_ANCHO
  canvas.height = SALIDA_ALTO
  const ctx = canvas.getContext('2d')
  dibujarFondoCotizacion(ctx, leyenda)

  fotos.forEach((foto, i) => {
    const p = posiciones[i]
    ctx.save()
    ctx.translate(p.x, p.y)
    const visible = tamanoVisible(foto)
    ctx.scale(p.w / visible.w, p.h / visible.h)
    dibujarFotoConCotas(ctx, foto)
    ctx.restore()
  })

  // Los textos van encima de todo.
  textos.forEach((t) => dibujarTexto(ctx, t))

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('No se pudo exportar la imagen'))),
      'image/jpeg',
      0.93,
    )
  })
}

/** Versión del JSON de edición guardado en la revisión (`json_cotizacion`). */
export const COTIZACION_JSON_V = 1

/**
 * Snapshot serializable del editor (sin blobs ni HTMLImageElement).
 * Se guarda en `json_cotizacion` de la revisión para continuar o volver a editar.
 */
export const serializarCotizacion = ({
  paso,
  seleccion = [],
  cotas = {},
  extensiones = {},
  quitarMargenes = true,
  capas = [],
  textos = [],
  medidasManuales = null,
}) => ({
  v: COTIZACION_JSON_V,
  paso: paso === 'resultado' ? 'resultado' : 'editor',
  seleccion: [...seleccion],
  cotas: JSON.parse(JSON.stringify(cotas || {})),
  extensiones: JSON.parse(JSON.stringify(extensiones || {})),
  quitarMargenes: !!quitarMargenes,
  capas: Array.isArray(capas)
    ? capas.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }))
    : [],
  textos: Array.isArray(textos)
    ? textos.map((t) => ({
        id: t.id,
        texto: t.texto,
        x: t.x,
        y: t.y,
        tamano: t.tamano,
        color: t.color,
        negrita: !!t.negrita,
        fondo: !!t.fondo,
      }))
    : [],
  ...(Array.isArray(medidasManuales) && medidasManuales.length
    ? { medidasManuales: JSON.parse(JSON.stringify(medidasManuales)) }
    : {}),
})

/** Acepta objeto o string JSON (así a veces llega del backend). */
export const parseJsonCotizacion = (raw) => {
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
  return typeof raw === 'object' ? raw : null
}

/**
 * Filtra un snapshot a las fotos que aún existen.
 * Conserva `paso` del JSON; si las capas no cuadran se descartan (el caller
 * puede regenerarlas al ir a composición).
 *
 * @returns {null | object} null si el JSON no es usable
 */
export const hidratarCotizacion = (jsonRaw, fotosDisponibles = []) => {
  const json = parseJsonCotizacion(jsonRaw)
  if (!json || typeof json !== 'object') return null
  const nombresOk = new Set(
    (fotosDisponibles || []).map((f) => f?.nombre_objeto).filter(Boolean),
  )
  const seleccion = (Array.isArray(json.seleccion) ? json.seleccion : []).filter((n) =>
    nombresOk.has(n),
  )
  if (!seleccion.length) return null

  const cotasIn = json.cotas && typeof json.cotas === 'object' ? json.cotas : {}
  const extIn = json.extensiones && typeof json.extensiones === 'object' ? json.extensiones : {}
  const cotas = {}
  const extensiones = {}
  seleccion.forEach((n) => {
    if (cotasIn[n] && typeof cotasIn[n] === 'object') cotas[n] = cotasIn[n]
    if (extIn[n] && typeof extIn[n] === 'object') extensiones[n] = extIn[n]
  })

  const capasNormalizadas = (Array.isArray(json.capas) ? json.capas : [])
    .map((c) => {
      if (!c || typeof c !== 'object') return null
      const i = Number(c.i)
      const x = Number(c.x)
      const y = Number(c.y)
      const w = Number(c.w)
      const h = Number(c.h)
      if (![i, x, y, w, h].every(Number.isFinite) || w <= 0 || h <= 0) return null
      if (i < 0 || i >= seleccion.length) return null
      return { i, x, y, w, h }
    })
    .filter(Boolean)

  const capasValidas = capasNormalizadas.length === seleccion.length

  return {
    v: COTIZACION_JSON_V,
    paso: json.paso === 'resultado' ? 'resultado' : 'editor',
    seleccion,
    cotas,
    extensiones,
    quitarMargenes: json.quitarMargenes !== false,
    capas: capasValidas ? capasNormalizadas : [],
    textos: Array.isArray(json.textos) ? json.textos : [],
    medidasManuales: Array.isArray(json.medidasManuales) ? json.medidasManuales : null,
  }
}
